import React, { useEffect, useState } from 'react';
import { chapterImageUrl, slokaImageUrl } from '../utils/mediaUrls';

function SlokaArtPlaceholder({ chapterNumber, slokaNumber, loading, variant = 'light' }) {
  return (
    <div
      className={`sloka-art-placeholder sloka-art-placeholder--${variant} ${
        loading ? 'sloka-art-placeholder--loading' : ''
      }`}
      aria-hidden={loading}
    >
      <span className="sloka-art-placeholder-ornament">॥</span>
      <p className="sloka-art-placeholder-badge">
        Chapter {chapterNumber} · Verse {slokaNumber}
      </p>
      <p className="sloka-art-placeholder-label">
        {loading ? 'Loading illustration…' : 'Sacred verse art'}
      </p>
    </div>
  );
}

/**
 * Sloka illustration — full image (object-contain) inside a framed panel.
 * fallback="placeholder" shows a verse badge panel when art is missing (chapter pages).
 * fallback="chapter" uses chapter art as backup (players, thumbnails).
 */
const SlokaArtImage = ({
  chapterNumber,
  slokaNumber,
  alt = '',
  className = '',
  frameClassName = '',
  frameVariant = 'light',
  fallback = 'chapter',
  framed = false,
  ...props
}) => {
  const slokaUrl =
    slokaNumber != null && chapterNumber != null
      ? slokaImageUrl(chapterNumber, slokaNumber)
      : null;
  const chapterUrl = chapterNumber != null ? chapterImageUrl(chapterNumber) : null;

  const [phase, setPhase] = useState('loading');
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!framed) {
      setSrc(slokaUrl || (fallback === 'chapter' ? chapterUrl : null));
      setPhase(slokaUrl || chapterUrl ? 'ready' : 'missing');
      return undefined;
    }

    if (!slokaUrl) {
      if (fallback === 'chapter' && chapterUrl) {
        setSrc(chapterUrl);
        setPhase('ready');
      } else {
        setSrc(null);
        setPhase('missing');
      }
      return undefined;
    }

    setPhase('loading');
    setSrc(null);

    const img = new Image();
    img.onload = () => {
      setSrc(slokaUrl);
      setPhase('ready');
    };
    img.onerror = () => {
      if (fallback === 'chapter' && chapterUrl) {
        setSrc(chapterUrl);
        setPhase('ready');
      } else {
        setSrc(null);
        setPhase('missing');
      }
    };
    img.src = slokaUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [slokaUrl, chapterUrl, fallback, framed]);

  const showPlaceholder = phase === 'loading' || phase === 'missing';
  const showImage = phase === 'ready' && src;

  const imageEl = showImage ? (
    <img
      src={src}
      alt={alt}
      className={`sloka-art-img ${className}`}
      loading="lazy"
      onError={() => {
        if (fallback === 'chapter' && chapterUrl && src !== chapterUrl) {
          setSrc(chapterUrl);
          setPhase('ready');
          return;
        }
        setSrc(null);
        setPhase('missing');
      }}
      {...props}
    />
  ) : null;

  if (!framed) {
    return imageEl;
  }

  return (
    <div
      className={`sloka-art-frame sloka-art-frame--${frameVariant} ${frameClassName}`}
    >
      {showPlaceholder && (
        <SlokaArtPlaceholder
          chapterNumber={chapterNumber}
          slokaNumber={slokaNumber}
          loading={phase === 'loading'}
          variant={frameVariant}
        />
      )}
      {imageEl}
    </div>
  );
};

export default SlokaArtImage;
